import React, { useState } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  List,
  ListItem,
  ListItemText,
  IconButton,
  Typography,
  Box,
  Tooltip,
} from '@mui/material';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import AddIcon from '@mui/icons-material/Add';
import { useTemplatesModel } from '../hooks/useTemplatesModel';
import { useOnlineStatus } from '../hooks/useOnlineStatus';
import { stripHtml, NOTES_PREVIEW_LENGTH } from '../utils/bookmarkUtils';
import { ConfirmDialog } from './ConfirmDialog';
import AddTemplateDialog from './AddTemplateDialog';

interface TemplatePickerProps {
  open: boolean;
  onClose: () => void;
  /** Insert the chosen template's HTML content into the current note. */
  onInsert: (html: string) => void;
}

function preview(html: string): string {
  const text = stripHtml(html).trim();
  if (text.length <= NOTES_PREVIEW_LENGTH) return text;
  return text.slice(0, NOTES_PREVIEW_LENGTH).trimEnd() + '…';
}

/**
 * Lists templates for insertion into the open note, and supports managing them
 * (add / edit / delete). Insert returns the template HTML to the caller.
 */
const TemplatePicker: React.FC<TemplatePickerProps> = ({
  open,
  onClose,
  onInsert,
}) => {
  const { templates, loading, deleteTemplate } = useTemplatesModel();
  const { isOnline } = useOnlineStatus();

  const [addOpen, setAddOpen] = useState(false);
  const [editId, setEditId] = useState<string | null>(null);
  const [deleteId, setDeleteId] = useState<string | null>(null);

  const openEdit = (id: string) => {
    setEditId(id);
    setAddOpen(true);
  };

  const openAdd = () => {
    setEditId(null);
    setAddOpen(true);
  };

  const handleDeleteConfirm = async () => {
    if (deleteId) {
      await deleteTemplate(deleteId);
    }
    setDeleteId(null);
  };

  return (
    <>
      <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
        <DialogTitle>Insert Template</DialogTitle>
        <DialogContent dividers>
          {loading && (
            <Typography variant="body2" color="text.secondary">
              Loading templates…
            </Typography>
          )}
          {!loading && templates.length === 0 && (
            <Typography variant="body2" color="text.secondary">
              No templates yet. Create one to reuse boilerplate across notes.
            </Typography>
          )}
          <List disablePadding>
            {templates.map((t) => (
              <ListItem
                key={t.template_id}
                disablePadding
                secondaryAction={
                  <Box>
                    <Tooltip title="Edit">
                      <span>
                        <IconButton
                          edge="end"
                          size="small"
                          onClick={() => openEdit(t.template_id)}
                          disabled={!isOnline}
                          aria-label={`Edit ${t.template_name}`}
                        >
                          <EditIcon fontSize="small" />
                        </IconButton>
                      </span>
                    </Tooltip>
                    <Tooltip title="Delete">
                      <span>
                        <IconButton
                          edge="end"
                          size="small"
                          onClick={() => setDeleteId(t.template_id)}
                          disabled={!isOnline}
                          aria-label={`Delete ${t.template_name}`}
                        >
                          <DeleteIcon fontSize="small" />
                        </IconButton>
                      </span>
                    </Tooltip>
                  </Box>
                }
              >
                <Button
                  fullWidth
                  onClick={() => {
                    onInsert(t.template_content);
                    onClose();
                  }}
                  sx={{
                    justifyContent: 'flex-start',
                    textTransform: 'none',
                    textAlign: 'left',
                    py: 1,
                    pr: 10,
                  }}
                >
                  <ListItemText
                    primary={t.template_name}
                    secondary={preview(t.template_content)}
                    secondaryTypographyProps={{ noWrap: true }}
                  />
                </Button>
              </ListItem>
            ))}
          </List>
        </DialogContent>
        <DialogActions sx={{ justifyContent: 'space-between' }}>
          <Button
            startIcon={<AddIcon />}
            onClick={openAdd}
            disabled={!isOnline}
          >
            Add Template
          </Button>
          <Button onClick={onClose}>Close</Button>
        </DialogActions>
      </Dialog>

      <AddTemplateDialog
        open={addOpen}
        onClose={() => setAddOpen(false)}
        editTemplateId={editId}
      />

      <ConfirmDialog
        open={deleteId !== null}
        title="Delete template?"
        message="This template will be permanently deleted."
        onConfirm={handleDeleteConfirm}
        onCancel={() => setDeleteId(null)}
      />
    </>
  );
};

export default TemplatePicker;
